import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";
import { Calendar } from "lucide-react";
import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { useTranslation } from "@/packages/utils";
import { pipeline } from "./dashboard.data";

const totalAudits = pipeline.reduce((sum, s) => sum + s.count, 0);
const COMPLIANCE_SCORE = 87;

const chartData = pipeline.map((stage) => ({
  name: stage.status,
  value: stage.count,
  color: stage.color,
}));

function renderActiveShape(props: unknown) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props as {
      cx: number;
      cy: number;
      innerRadius: number;
      outerRadius: number;
      startAngle: number;
      endAngle: number;
      fill: string;
    };

  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius - 3}
      outerRadius={outerRadius + 4}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
}

function PipelineLegend({
  compact,
  activeStages,
  toggleStage,
}: {
  compact: boolean;
  activeStages: Set<string>;
  toggleStage: (status: string) => void;
}) {
  return (
    <div
      className={
        compact
          ? "flex flex-col gap-1"
          : "flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-2 border-t border-border"
      }
    >
      {pipeline.map((stage) => (
        <button
          key={stage.status}
          type="button"
          onClick={() => toggleStage(stage.status)}
          className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity outline-none"
        >
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: stage.color }}
          />
          <span
            className={`text-xs whitespace-nowrap transition-colors ${
              activeStages.has(stage.status)
                ? "text-primary font-semibold"
                : "text-secondary"
            }`}
          >
            {stage.status} ({stage.count})
          </span>
        </button>
      ))}
    </div>
  );
}

export function AuditPipeline() {
  const { t } = useTranslation(["dashboard"]);
  const [activeStages, setActiveStages] = useState<Set<string>>(new Set());

  const isCompact = activeStages.size > 0;

  const activeIndices = pipeline
    .map((s, i) => (activeStages.has(s.status) ? i : -1))
    .filter((i) => i >= 0);

  const toggleStage = useCallback((status: string) => {
    setActiveStages((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const handlePieClick = useCallback(
    (_: unknown, index: number) => {
      const stage = pipeline[index];
      if (stage) toggleStage(stage.status);
    },
    [toggleStage],
  );

  const activeAuditGroups = pipeline.filter((s) => activeStages.has(s.status));

  return (
    <div className="rounded-xl bg-card border border-border overflow-hidden h-full flex flex-col">
      <div className="flex items-center justify-between px-4 pt-3 pb-1 shrink-0">
        <h3 className="text-sm font-semibold text-primary">
          {t("dashboard:pipeline.title")}
        </h3>
        <span className="text-xs text-muted">
          {t("dashboard:pipeline.totalAudits", { count: totalAudits })}
        </span>
      </div>

      <div className="pipeline-body overflow-y-auto flex-1 min-h-0 px-4 pb-3">
        {/* Default: donut centered above, legend horizontal at bottom */}
        {/* Compact: donut left + legend stacked right */}
        <div
          className={`flex ${
            isCompact
              ? "flex-row items-start gap-3"
              : "flex-col items-center justify-between h-full"
          }`}
        >
          <div
            className={`pipeline-donut-wrap relative transition-all duration-300 ease-in-out shrink-0 ${
              isCompact ? "w-[130px] h-[130px]" : "w-full flex-1 min-h-0"
            }`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={isCompact ? 36 : "55%"}
                  outerRadius={isCompact ? 54 : "85%"}
                  paddingAngle={2}
                  dataKey="value"
                  animationDuration={800}
                  strokeWidth={0}
                  activeIndex={
                    activeIndices.length > 0 ? activeIndices : undefined
                  }
                  activeShape={renderActiveShape}
                  onClick={handlePieClick}
                  style={{ cursor: "pointer" }}
                >
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={entry.color}
                      opacity={
                        activeStages.size === 0 || activeStages.has(entry.name)
                          ? 1
                          : 0.35
                      }
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            <div className="pipeline-donut-center">
              <motion.span
                animate={{ fontSize: isCompact ? "18px" : "24px" }}
                transition={springs.snappy}
                className="font-extrabold text-primary tracking-tight leading-none"
              >
                {COMPLIANCE_SCORE}%
              </motion.span>
              <motion.span
                animate={{
                  opacity: isCompact ? 0 : 1,
                  height: isCompact ? 0 : "auto",
                  marginTop: isCompact ? 0 : 4,
                }}
                transition={springs.snappy}
                className="text-xs text-secondary font-medium overflow-hidden"
              >
                {t("dashboard:pipeline.compliance")}
              </motion.span>
            </div>
          </div>

          <div className={isCompact ? "pt-2" : "w-full shrink-0"}>
            <PipelineLegend
              compact={isCompact}
              activeStages={activeStages}
              toggleStage={toggleStage}
            />
          </div>
        </div>

        <AnimatePresence>
          {activeAuditGroups.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={springs.smooth}
              className="border-t border-border pt-3 mt-3"
            >
              {activeAuditGroups.map((group) => (
                <div key={group.status}>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary pb-1.5">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: group.color }}
                    />
                    {group.status}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {group.audits.map((audit) => (
                      <div
                        key={audit.name}
                        className="py-2 px-3 bg-surface rounded-lg hover:bg-surface/80 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-sm font-medium text-primary truncate">
                            {audit.name}
                          </span>
                          <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-card border border-border text-secondary whitespace-nowrap shrink-0">
                            {audit.fw}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted">
                            {audit.client}
                          </span>
                          <div className="flex items-center gap-3 shrink-0">
                            {audit.p !== undefined && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-14 h-1.5 bg-border rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-blue-500 transition-[width] duration-500"
                                    style={{ width: `${audit.p}%` }}
                                  />
                                </div>
                                <span className="text-xs text-secondary">
                                  {audit.p}%
                                </span>
                              </div>
                            )}
                            <div className="text-xs text-muted flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {audit.due}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
